// frontend/src/components/annual-addition-section/EnergyAdditionTemplate.js
import { useEffect, useState } from 'react';
import CustomDatePicker from './CustomDatePicker';
import { useEnergyAdditionData } from '../hooks/useEnergyAdditionData';
import EnergyAdditionBarChart from './EnergyAdditionBarChart';

/**
 * 
 * EnergyAdditionChart component displays a bar chart for energy additions.
 * It functions as a template for energy addition data visualization.
 * It allows users to toggle between annual and monthly views, select a date range,
 * and visualize energy addition data based on the selected parameters.
 * @param {*} source - The source of the energy data (e.g., "PV", "WP"). 
 * @param {*} location_key - The key for the location to filter data, e.g. "Sachsen_Ort_Leipzig"
 * @param {*} title - Title of the chart, e.g. "Historischer Zubau an Photovoltaikanlagen"
 * @param {*} barColor - color of bars in chart, e.g. "#ffcd00"
 * @param {*} unit - Unit of measurement for the values (e.g., "MW", "MWp").
 * @returns 
 */

function EnergyAdditionChart({ source, location_key, title, barColor, unit }) {
  const [isAnnual, setIsAnnual] = useState(true);
  const currentYear = new Date().getFullYear();

  // Default date range: last 5 years
  const defaultRange = [
    new Date(currentYear - 5, 0, 1),
    new Date(currentYear, 11, 31),
  ];

  // State for date picker range
  const [pickerRange, setPickerRange] = useState(defaultRange);
  const [selectedRange, setSelectedRange] = useState(defaultRange);

  // if both picker values are set, update selectedRange
  useEffect(() => {
    const [start, end] = pickerRange;
    if (start && end) {
      setSelectedRange([start, end]);
    }
  }, [pickerRange]);

  // Extract start and end dates from selectedRange
  const [selectedStart, selectedEnd] = selectedRange;
  // Ensure start and end dates are valid
  const shouldLoadData = selectedStart && selectedEnd && source && location_key;

  // Load energy addition data based on selected parameters
  const energyData = useEnergyAdditionData(
    shouldLoadData
      ? {
          source,
          locationKey: location_key,
          isAnnual,
          startDate: selectedStart,
          endDate: selectedEnd,
        }
      : {}
  );

  // Destructure data for easier access
  const {
    labels,
    values,
    tooltipPowerAddition,
    tooltipPowerReduction,
    tooltipCountAddition,
    tooltipCountReduction,
    loading,
    error,
  } = energyData;

  return (
    <div className="m-5 p-5 bg-white border shadow-lg rounded-lg">
      <h3 className="text-xl font-bold">{title}</h3>

      <div className="flex space-x-4 my-4 justify-center">
        <button
          onClick={() => setIsAnnual(true)}
          className={`px-4 py-2 rounded ${isAnnual ? 'bg-black text-white' : 'bg-gray-200'}`}
        >
          Jährlich
        </button>
        <button
          onClick={() => setIsAnnual(false)}
          className={`px-4 py-2 rounded ${!isAnnual ? 'bg-black text-white' : 'bg-gray-200'}`}
        >
          Monatlich
        </button>
      </div>

      <div className="my-4">
        <p className="text-gray-600 pb-5">Bitte den gewünschten Zeitraum auswählen:</p>
        <CustomDatePicker
          isAnnual={isAnnual}
          startDate={pickerRange[0]}
          endDate={pickerRange[1]}
          onChange={(range) => setPickerRange(range)}
        />
      </div>

      <div className="h-96 w-full mx-auto flex items-center justify-center">
        {loading ? (
          <p>Lade Daten...</p>
        ) : error ? (
          <p className="text-red-500">Fehler beim Laden der Daten: {error.message}</p>
        ) : labels.length === 0 ? (
          <p className="text-gray-500">Keine Daten im gewählten Zeitraum</p>
        ) : (
          <EnergyAdditionBarChart
            values={values}
            labels={labels}
            chartColor={barColor}
            yAxisLabel={`Netto Zubau (${unit})`}
            isAnnual={isAnnual}
            countAddition={tooltipCountAddition}
            countReduction={tooltipCountReduction}
            powerAddition={tooltipPowerAddition}
            powerReduction={tooltipPowerReduction}
            unit={unit}
          />
        )}
      </div>
    </div>
  );
}

export default EnergyAdditionChart;
